/**
 * Offline-First API Client for MedTranslate
 *
 * Wraps API calls to:
 * 1. Check local cache first
 * 2. Try network if online
 * 3. Fall back to cache if offline
 * 4. Queue operations for later sync
 */

import api from '../api/client';
import * as db from './db';

// Online status tracking
let isOnline = navigator.onLine;

window.addEventListener('online', () => {
  isOnline = true;
  syncQueuedOperations();
  window.dispatchEvent(new CustomEvent('connectivity-change', { detail: { online: true } }));
});

window.addEventListener('offline', () => {
  isOnline = false;
  window.dispatchEvent(new CustomEvent('connectivity-change', { detail: { online: false } }));
});

/**
 * Check if currently online
 */
export function getOnlineStatus() {
  return isOnline;
}

/**
 * Subscribe to connectivity changes
 */
export function onConnectivityChange(callback) {
  const handler = (e) => callback(e.detail.online);
  window.addEventListener('connectivity-change', handler);
  return () => window.removeEventListener('connectivity-change', handler);
}

// ============ Documents API ============

/**
 * Get all documents (cache-first, then sync with server)
 */
export async function getDocuments() {
  // Always return cached documents first
  const cachedDocs = await db.getAllDocuments();

  if (!isOnline) {
    return { data: cachedDocs, fromCache: true };
  }

  try {
    const response = await api.get('/documents/');
    const serverDocs = response.data;

    // Update local cache with server data
    for (const doc of serverDocs) {
      await db.saveDocument(doc);
    }

    return { data: serverDocs, fromCache: false };
  } catch (error) {
    console.warn('Failed to fetch documents from server, using cache:', error.message);
    return { data: cachedDocs, fromCache: true };
  }
}

/**
 * Get a single document by ID
 */
export async function getDocument(documentId) {
  // Try cache first
  const cachedDoc = await db.getDocument(documentId);

  if (!isOnline) {
    if (cachedDoc) {
      return { data: cachedDoc, fromCache: true };
    }
    throw new Error('Document not available offline');
  }

  try {
    const response = await api.get(`/documents/${documentId}`);
    const doc = response.data;

    // Cache the document
    await db.saveDocument(doc);

    return { data: doc, fromCache: false };
  } catch (error) {
    if (cachedDoc) {
      console.warn('Failed to fetch document from server, using cache:', error.message);
      return { data: cachedDoc, fromCache: true };
    }
    throw error;
  }
}

/**
 * Upload a document (requires online)
 */
export async function uploadDocument(formData) {
  if (!isOnline) {
    // Queue for later upload
    const file = formData.get('file');
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        const pendingDoc = {
          id: `pending_${Date.now()}`,
          filename: file.name,
          fileData: reader.result,
          fileType: file.type,
          status: 'pending_upload',
          createdAt: new Date().toISOString(),
          synced: false,
        };

        await db.saveDocument(pendingDoc);
        await db.addToSyncQueue({
          type: 'upload',
          documentId: pendingDoc.id,
        });

        resolve({
          data: pendingDoc,
          queued: true,
          message: 'Document saved locally. Will upload when online.',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const response = await api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  // Cache the uploaded document
  await db.saveDocument(response.data);

  return { data: response.data, fromCache: false };
}

// ============ Translation API ============

/**
 * Get a translation (cache-first)
 */
export async function getTranslation(documentId, language) {
  // Check cache first
  const cachedTranslation = await db.getTranslation(documentId, language);

  if (cachedTranslation) {
    // Even if online, return cached translation (they don't change)
    return { data: cachedTranslation, fromCache: true };
  }

  if (!isOnline) {
    throw new Error('Translation not available offline. Connect to internet to translate.');
  }

  // Fetch from server
  try {
    const response = await api.get(`/translate/${documentId}?language=${language}`);
    const translation = response.data;

    if (translation && translation.translated_text) {
      await db.saveTranslation({
        ...translation,
        documentId,
        language,
      });
    }

    return { data: translation, fromCache: false };
  } catch (error) {
    throw error;
  }
}

/**
 * Request a new translation
 */
export async function requestTranslation(documentId, language) {
  // Check if we already have this translation
  const cached = await db.getTranslation(documentId, language);
  if (cached && cached.translated_text) {
    return { data: cached, fromCache: true };
  }

  if (!isOnline) {
    // Queue translation request
    await db.addToSyncQueue({
      type: 'translate',
      documentId,
      language,
      createdAt: new Date().toISOString(),
    });

    return {
      data: null,
      queued: true,
      message: `Translation to ${language} will be generated when online.`,
    };
  }

  const response = await api.post('/translate/', { document_id: documentId, language });
  const translation = response.data;

  // Cache the translation
  await db.saveTranslation({
    ...translation,
    documentId,
    language,
  });

  return { data: translation, fromCache: false };
}

// ============ Chat API ============

/**
 * Get chat history for a document
 */
export async function getChatHistory(documentId) {
  const cachedMessages = await db.getChatMessages(documentId);

  if (!isOnline) {
    return { data: cachedMessages, fromCache: true };
  }

  try {
    const response = await api.get(`/chat/${documentId}`);
    const serverMessages = response.data.messages || [];

    // Merge server messages with local messages
    for (const msg of serverMessages) {
      await db.saveChatMessage({ ...msg, documentId });
    }

    // Return combined messages
    const allMessages = await db.getChatMessages(documentId);
    return { data: allMessages, fromCache: false };
  } catch (error) {
    console.warn('Failed to fetch chat history, using cache:', error.message);
    return { data: cachedMessages, fromCache: true };
  }
}

/**
 * Send a chat message
 */
export async function sendChatMessage(documentId, message, language = 'English') {
  // Save user message locally immediately
  const userMessage = {
    id: `msg_${Date.now()}_user`,
    documentId,
    role: 'user',
    content: message,
    language,
    createdAt: new Date().toISOString(),
    synced: isOnline,
  };
  await db.saveChatMessage(userMessage);

  if (!isOnline) {
    // Queue for later
    await db.addToSyncQueue({
      type: 'chat',
      documentId,
      message,
      language,
      messageId: userMessage.id,
    });

    return {
      userMessage,
      assistantMessage: null,
      queued: true,
      message: 'Your question will be answered when online.',
    };
  }

  try {
    const response = await api.post('/chat/', {
      document_id: documentId,
      message,
      language,
    });

    const assistantMessage = {
      id: `msg_${Date.now()}_assistant`,
      documentId,
      role: 'assistant',
      content: response.data.response,
      language,
      createdAt: new Date().toISOString(),
      synced: true,
    };
    await db.saveChatMessage(assistantMessage);

    return { userMessage, assistantMessage, fromCache: false };
  } catch (error) {
    // Queue for retry
    await db.addToSyncQueue({
      type: 'chat',
      documentId,
      message,
      language,
      messageId: userMessage.id,
    });

    throw error;
  }
}

// ============ Dashboard API ============

/**
 * Get dashboard stats
 */
export async function getDashboardStats() {
  if (!isOnline) {
    // Return local stats
    const stats = await db.getStorageStats();
    return {
      data: {
        total_documents: stats.documents,
        total_translations: stats.translations,
        total_chats: stats.chatMessages,
        offline: true,
      },
      fromCache: true,
    };
  }

  try {
    const response = await api.get('/dashboard/stats');
    return { data: response.data, fromCache: false };
  } catch (error) {
    const stats = await db.getStorageStats();
    return {
      data: {
        total_documents: stats.documents,
        total_translations: stats.translations,
        total_chats: stats.chatMessages,
        offline: true,
      },
      fromCache: true,
    };
  }
}

// ============ Languages API ============

let cachedLanguages = null;

export async function getLanguages() {
  if (cachedLanguages) {
    return { data: cachedLanguages, fromCache: true };
  }

  // Try to get from app state
  const stored = await db.getAppState('languages');
  if (stored) {
    cachedLanguages = stored;
    if (!isOnline) {
      return { data: stored, fromCache: true };
    }
  }

  if (!isOnline && !stored) {
    // Return default languages
    const defaults = [
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Spanish' },
      { code: 'hi', name: 'Hindi' },
      { code: 'pt', name: 'Portuguese' },
      { code: 'ht', name: 'Haitian Creole' },
      { code: 'zh', name: 'Mandarin Chinese' },
    ];
    return { data: defaults, fromCache: true };
  }

  try {
    const response = await api.get('/languages/');
    cachedLanguages = response.data;
    await db.setAppState('languages', response.data);
    return { data: response.data, fromCache: false };
  } catch (error) {
    if (stored) {
      return { data: stored, fromCache: true };
    }
    throw error;
  }
}

// ============ Sync Operations ============

/**
 * Process queued operations when back online
 */
export async function syncQueuedOperations() {
  if (!isOnline) return { synced: 0, failed: 0 };

  const queue = await db.getSyncQueue();
  let synced = 0;
  let failed = 0;

  for (const item of queue) {
    try {
      switch (item.type) {
        case 'upload':
          // Handle pending uploads
          const pendingDoc = await db.getDocument(item.documentId);
          if (pendingDoc && pendingDoc.fileData) {
            const blob = await fetch(pendingDoc.fileData).then((r) => r.blob());
            const formData = new FormData();
            formData.append('file', blob, pendingDoc.filename);

            const response = await api.post('/documents/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Update local document with server response
            await db.deleteDocument(item.documentId);
            await db.saveDocument(response.data);
          }
          break;

        case 'translate':
          await requestTranslation(item.documentId, item.language);
          break;

        case 'chat':
          // Re-send chat message
          const doc = await db.getDocument(item.documentId);
          if (doc) {
            await api.post('/chat/', {
              document_id: item.documentId,
              message: item.message,
              language: item.language,
            });
          }
          break;
      }

      await db.removeFromSyncQueue(item.id);
      synced++;
    } catch (error) {
      console.error(`Failed to sync ${item.type}:`, error);
      failed++;
    }
  }

  if (synced > 0) {
    window.dispatchEvent(new CustomEvent('sync-complete', { detail: { synced, failed } }));
  }

  return { synced, failed };
}

/**
 * Get pending sync count
 */
export async function getPendingSyncCount() {
  const queue = await db.getSyncQueue();
  return queue.length;
}
